import { Link } from 'react-router-dom';
import { hasScope } from '@nyce/admin-kit/api';
import { Chip } from '@nyce/admin-kit/ui';
import { SCOPES } from '../api';
import { useHubLogin } from '../session';
import { identityPath, useTargets } from '../identity/session';
import { ApiError } from '@nyce/admin-kit/api';
import { formatDateTime, formatRelative } from '../lib/format';
import type {
  JobStatus,
  PrintJob,
  PrintQueueApi,
  PrintServer,
  PrintServerLogsState,
  PrintServerReleases,
  PrintServerUpdateState,
  PrintUsage,
  UsageQuery,
} from './api/client';
import { linkOf, type PrintQueueLinks } from './links';

const TONE: Record<JobStatus, string> = {
  Registered: 'blue',
  Fetched: 'amber',
  Printing: 'amber',
  Printed: 'green',
  Error: 'red',
};

/** Jobs still with a print server longer than this look stuck; waiting longer = the print server is not polling. */
export const STUCK_MS = 15 * 60_000;

export function JobStatusChip({ status }: { status: JobStatus }) {
  return (
    <Chip size="sm" className={`oph-status nyk-tone-${TONE[status] ?? 'grey'}`} data-status={status}>
      {status}
    </Chip>
  );
}

const ms = (t: string | null | undefined) => (t ? new Date(t).getTime() : NaN);

/** Unfinished and untouched for {@link STUCK_MS}: waiting (`Registered`) or held by a print server. */
export const isStuck = (j: PrintJob, now = Date.now()) =>
  (j.status === 'Registered' || j.status === 'Fetched' || j.status === 'Printing') &&
  now - (Number.isFinite(ms(j.modificationDate)) ? ms(j.modificationDate) : ms(j.dateAdded)) > STUCK_MS;

const STATE_TONE = { live: 'green', polling: 'blue', offline: 'grey' } as const;

/** A print server's state: live (hub) / polling / offline with when it was last seen; `unknown` = before 1.5.0. */
export function PrintServerStateChip({ server }: { server: PrintServer | null | undefined }) {
  if (!server)
    return (
      <Chip
        size="sm"
        className="oph-status nyk-tone-grey"
        title="The print queue does not record print server contacts (before 1.5.0)."
      >
        unknown
      </Chip>
    );
  const label =
    server.state === 'offline'
      ? server.lastSeenAt
        ? `offline · ${formatRelative(server.lastSeenAt)}`
        : 'not seen'
      : server.state;
  const title = [
    server.lastSeenAt && `Last contact ${formatDateTime(server.lastSeenAt)}`,
    server.lastHubAt && `hub ${formatRelative(server.lastHubAt)}`,
    server.lastPollAt && `poll ${formatRelative(server.lastPollAt)}`,
    server.lastUploadAt && `printers uploaded ${formatRelative(server.lastUploadAt)}`,
    server.userAgent,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Chip
      size="sm"
      className={`oph-status nyk-tone-${STATE_TONE[server.state] ?? 'grey'}`}
      data-state={server.state}
      title={title || undefined}
    >
      {label}
    </Chip>
  );
}

/** Print servers with their state, or null when the print queue is older than 1.5.0 (404). */
export async function printServersOrNull(
  api: PrintQueueApi,
  tenantId?: string
): Promise<PrintServer[] | null> {
  try {
    return await api.printServers(tenantId);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

/** Releases print servers can update to, or null from a print queue before 1.8.0 (no `updates` route). */
export async function releasesOrNull(api: PrintQueueApi): Promise<PrintServerReleases | null> {
  try {
    return await api.releases();
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 403)) return null;
    throw e;
  }
}

/** The newest release that is not a prerelease. */
export const latestRelease = (releases: PrintServerReleases | null) =>
  releases?.releases.find((r) => !r.prerelease)?.version ?? null;

/** "3.10.0" > "3.9.1"; a prerelease (3.0.0-beta) before its release. */
export function compareVersions(a: string, b: string): number {
  const [na, pa] = a.split('+')[0].split(/-(.*)/);
  const [nb, pb] = b.split('+')[0].split(/-(.*)/);
  const xa = na.split('.').map(Number);
  const xb = nb.split('.').map(Number);
  for (let i = 0; i < Math.max(xa.length, xb.length); i++) {
    const d = (xa[i] ?? 0) - (xb[i] ?? 0);
    if (d) return d;
  }
  if (pa === pb) return 0;
  if (!pa) return 1;
  if (!pb) return -1;
  return pa < pb ? -1 : 1;
}

const UPDATE_TONE: Record<PrintServerUpdateState, string> = {
  requested: 'blue',
  sent: 'blue',
  downloading: 'blue',
  verifying: 'blue',
  draining: 'amber',
  restarting: 'amber',
  done: 'green',
  failed: 'red',
};

/** The print server's last update: waiting, under way, done or failed (with why). Nothing when never asked. */
export function UpdateStateChip({ server }: { server: PrintServer | null | undefined }) {
  if (!server?.updateState || !server.updateTargetVersion) return null;
  const label =
    server.updateState === 'done'
      ? `updated to ${server.updateTargetVersion}`
      : `${server.updateState} ${server.updateTargetVersion}`;
  const title = [
    server.updateMessage,
    server.updateStateAt && `since ${formatDateTime(server.updateStateAt)}`,
    server.updateRequestedAt && `asked ${formatRelative(server.updateRequestedAt)}`,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Chip
      size="sm"
      className={`oph-status nyk-tone-${UPDATE_TONE[server.updateState] ?? 'grey'}`}
      data-update={server.updateState}
      title={title || undefined}
    >
      {label}
    </Chip>
  );
}

/** The print server version that can send its logs (with print queue 1.12.0). */
export const LOGS_MIN_VERSION = '3.1.0';

/** Whether the print server reported a version that can send its logs. */
export const canSendLogs = (server: PrintServer | null | undefined) =>
  !!server?.version && compareVersions(server.version, LOGS_MIN_VERSION) >= 0;

/** Waiting for the print server: the request is not answered yet. */
export const logsWaiting = (server: PrintServer | null | undefined) =>
  server?.logsState === 'requested' || server?.logsState === 'sent';

const LOGS: Record<PrintServerLogsState, { tone: string; label: string; title: string }> = {
  requested: { tone: 'blue', label: 'logs: waiting', title: 'Not sent to the print server yet' },
  sent: { tone: 'blue', label: 'logs: collecting', title: 'Sent to the print server, waiting for its zip' },
  ready: { tone: 'green', label: 'logs ready', title: 'Uploaded; the print queue keeps them 7 days' },
  failed: { tone: 'red', label: 'logs failed', title: 'The print server did not send its logs' },
};

/** The print server's logs request: waiting, ready or failed (with why). Nothing when none. */
export function LogsStateChip({ server }: { server: PrintServer | null | undefined }) {
  const state = server?.logsState;
  if (!state || !LOGS[state]) return null;
  const s = LOGS[state];
  const title = [
    state === 'failed' ? server.logsMessage : s.title,
    server.logsStateAt && `since ${formatDateTime(server.logsStateAt)}`,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Chip size="sm" className={`oph-status nyk-tone-${s.tone}`} data-logs={state} title={title}>
      {s.label}
    </Chip>
  );
}

/** Usage counters, or null from a print queue before 1.7.0 (no `usage` route). */
export async function usageOrNull(api: PrintQueueApi, q: UsageQuery): Promise<PrintUsage | null> {
  try {
    return await api.usage(q);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

export const serverKey = (tenantId: string, name: string) => `${tenantId}\u0000${name}`;

/** The environment picked in the shell (`?target=`). */
export const usePrintQueueTarget = () => useTargets().active;

/** Same page, same environment: links keep `?target=`. */
export const pqPath = (path: string, target: string) =>
  `${path}${path.includes('?') ? '&' : '?'}target=${encodeURIComponent(target)}`;

export function usePrintQueueWrite(): boolean {
  return hasScope(useHubLogin()?.scopes, SCOPES.printqueueWrite);
}

/**
 * A print-queue TenantId with its AUTH tenant / customer / environments when a `printqueue:` alias links it; a TenantId
 * that is an AUTH tenant's id shows that tenant's name first.
 */
export function TenantCell({
  tenantId,
  links,
  target,
}: {
  tenantId: string;
  links: PrintQueueLinks;
  target: string;
}) {
  const l = linkOf(links, tenantId);
  if (!l.tenant)
    return (
      <span title={links.reason ?? `No AUTH tenant has the alias printqueue:${tenantId}`}>
        <b>{tenantId}</b>
      </span>
    );
  const authLink = (
    <Link to={identityPath(`/identity/tenants/${encodeURIComponent(l.tenant.id)}`, target)}>
      {l.tenant.name}
    </Link>
  );
  return (
    <span>
      <b>{l.own ? authLink : tenantId}</b>
      <div className="nyk-ui-hint">
        {l.own ? tenantId : authLink}
        {l.customer && ` · ${l.customer}`}
        {l.environments.length > 0 && ` · ${l.environments.map((e) => e.name).join(', ')}`}
      </div>
    </span>
  );
}

/** Label for a tenant select option: `ACME — Acme AB (nlc-prod-acme)`. */
export function tenantLabel(tenantId: string, links: PrintQueueLinks) {
  const l = linkOf(links, tenantId);
  if (!l.tenant) return tenantId;
  const env = l.environments.map((e) => e.name).join(', ');
  if (l.own) return `${l.tenant.name}${l.customer ? ` — ${l.customer}` : ''}${env ? ` (${env})` : ''}`;
  return `${tenantId} — ${l.customer ?? l.tenant.name}${env ? ` (${env})` : ''}`;
}

/**
 * The version a print server reported (3.0+) with its platform; an older one shows its User-Agent, if any. One not
 * run by the bootstrapper cannot be updated from here.
 */
export function VersionCell({ server, latest }: { server: PrintServer | null; latest?: string | null }) {
  if (!server?.version)
    return server?.userAgent ? (
      <span
        className="nyk-ui-hint"
        title="Older than 3.0: update it by hand once (install.ps1, docker compose pull)"
      >
        {server.userAgent}
      </span>
    ) : (
      <span className="nyk-ui-hint">—</span>
    );
  const outdated = !!latest && compareVersions(server.version, latest) < 0;
  return (
    <span className="nyk-inline" title={server.userAgent ?? undefined} data-version={server.version}>
      <span>{server.version}</span>
      {server.platform && <span className="nyk-ui-hint">{server.platform}</span>}
      {outdated && (
        <span className="nyk-text-amber" data-testid="pq-outdated">
          {latest} available
        </span>
      )}
      {!server.updatable && <span className="nyk-ui-hint">not updatable</span>}
    </span>
  );
}
